/**
 * Prepínanie obrazoviek podľa URL (hash), nech každá prežije refresh.
 * tabletop.dustbox.app je **portál** pre viac hier — Unmatched je jedna z nich:
 *
 *   #/                       portál Tabletop — zoznam hier
 *   #/patch-notes[/<oblasť>] patch notes portálu a hier (staré #/novinky presmeruje)
 *   #/unmatched              klubovňa Unmatched — moje stoly, výsledky
 *   #/unmatched/novy         nový stôl — výber bojovníkov a arény
 *   #/unmatched/kniznica     knižnica kariet (voliteľne …/kniznica/<hrdina>)
 *   #/unmatched/zapasy       všetky zápasy všetkých hráčov (prehľad)
 *   #/unmatched/g/<uuid>     zápas (aj jeho predsieň, kým sa čaká na súpera)
 *   #/dune[/…]               Dune — plná verzia 1.0, vlastný modul
 *   #/karak[/…]              Karak — vlastný modul
 *   #/mura[/…]               Mura: Hlas z podzemia — plošinovka pre jedného hráča
 *
 * Staré adresy (`#/g/<uuid>`, `#/novy`, `#/kniznica`) sa presmerujú — pozvánky
 * poslané pred portálom musia fungovať ďalej.
 */

import { lazy, Suspense, useCallback, useEffect, useState } from 'react';

import { ApiError, auth, type SessionUser } from './api.js';
import { Gate } from './portal/Gate.js';
import { Game } from './games/unmatched/screens/Game.js';
import { Home } from './games/unmatched/screens/Home.js';
import { Library } from './games/unmatched/screens/Library.js';
import { Matches } from './games/unmatched/screens/Matches.js';
import { NewTable } from './games/unmatched/screens/NewTable.js';
import { Changelog } from './portal/Changelog.js';
import { Portal } from './portal/Portal.js';

const DuneApp = lazy(() => import('./games/dune/DuneApp.js').then((m) => ({ default: m.DuneApp })));
const KarakApp = lazy(() => import('./games/karak/KarakApp.js').then((m) => ({ default: m.KarakApp })));
const MuraApp = lazy(() => import('./games/mura/MuraApp.js').then((m) => ({ default: m.MuraApp })));
import { AppHeader } from './portal/AppHeader.js';

/** Režim nového stola v adrese — `bot` je v API `solo`. */
export type NewMode = 'bot' | 'online' | 'hotseat';

export type Route =
  | { name: 'portal' }
  /** Patch notes: `scope` = portál alebo hra (`unmatched`, `dune`…), `all` = celá história. */
  | { name: 'changelog'; scope?: string }
  /** Dune: celá hra je vo vlastnom module; `path` je zvyšok adresy za `#/dune`. */
  | { name: 'dune'; path: string }
  /** Karak: vlastný modul; `path` je zvyšok adresy za `#/karak`. */
  | { name: 'karak'; path: string }
  /** Mura: vlastný modul; `path` je zvyšok adresy za `#/mura`. */
  | { name: 'mura'; path: string }
  | { name: 'home' }
  /** `mode` = režim predvolený z dlaždice klubovne (bot / online / hot-seat). */
  | { name: 'new'; mode?: NewMode }
  | { name: 'library'; heroId: string | null }
  | { name: 'matches' }
  | { name: 'match'; id: string };

function parseRoute(hash: string): Route {
  // Staré adresy spred portálu: #/g/…, #/novy, #/kniznica → pod #/unmatched.
  const path = /^#\/(g\/|novy$|kniznica)/.test(hash) ? hash.replace(/^#\//, '#/unmatched/') : hash;
  const match = /^#\/unmatched\/g\/([0-9a-f-]{36})$/i.exec(path);
  if (match) return { name: 'match', id: match[1]! };
  const fresh = /^#\/unmatched\/novy(?:\/(bot|online|hotseat))?$/.exec(path);
  if (fresh) return fresh[1] ? { name: 'new', mode: fresh[1] as NewMode } : { name: 'new' };
  if (path === '#/unmatched/zapasy') return { name: 'matches' };
  const library = /^#\/unmatched\/kniznica(?:\/([a-z0-9_-]+))?$/i.exec(path);
  if (library) return { name: 'library', heroId: library[1] ?? null };
  if (path === '#/unmatched' || path === '#/unmatched/') return { name: 'home' };
  if (path === '#/novinky') return { name: 'changelog' };
  const notes = /^#\/patch-notes(?:\/([a-z-]+))?$/.exec(path);
  if (notes) return notes[1] ? { name: 'changelog', scope: notes[1] } : { name: 'changelog' };
  const dune = /^#\/dune(\/.*)?$/.exec(path);
  if (dune) return { name: 'dune', path: dune[1] ?? '' };
  const karak = /^#\/karak(\/.*)?$/.exec(path);
  if (karak) return { name: 'karak', path: karak[1] ?? '' };
  const muraRoute = /^#\/mura(\/.*)?$/.exec(path);
  if (muraRoute) return { name: 'mura', path: muraRoute[1] ?? '' };
  return { name: 'portal' };
}

export function routeHash(route: Route): string {
  switch (route.name) {
    case 'match':
      return `#/unmatched/g/${route.id}`;
    case 'new':
      return route.mode ? `#/unmatched/novy/${route.mode}` : '#/unmatched/novy';
    case 'matches':
      return '#/unmatched/zapasy';
    case 'library':
      return route.heroId ? `#/unmatched/kniznica/${route.heroId}` : '#/unmatched/kniznica';
    case 'home':
      return '#/unmatched';
    case 'dune':
      return `#/dune${route.path}`;
    case 'karak':
      return `#/karak${route.path}`;
    case 'mura':
      return `#/mura${route.path}`;
    case 'changelog':
      return route.scope ? `#/patch-notes/${route.scope}` : '#/patch-notes';
    default:
      return '#/';
  }
}

export function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));

  // Stará adresa sa v lište prepíše na novú, aby sa ďalej zdieľala už tá.
  useEffect(() => {
    const canonical = routeHash(route);
    if (window.location.hash && window.location.hash !== canonical && route.name !== 'portal') {
      window.history.replaceState(null, '', canonical);
    }
  }, [route]);

  useEffect(() => {
    document.title =
      route.name === 'portal'
        ? 'Hearth & Table'
        : route.name === 'changelog'
          ? 'Patch notes · Hearth & Table'
          : route.name === 'dune'
            ? 'Dune · Hearth & Table'
            : route.name === 'karak'
              ? 'Karak · Hearth & Table'
              : route.name === 'mura'
                ? 'Mura · Hearth & Table'
                : 'Unmatched · Hearth & Table';
  }, [route.name]);

  useEffect(() => {
    void (async () => {
      try {
        setUser((await auth.me()).user);
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 401)) console.error(err);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onHashChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const go = useCallback(
    (next: Route) => {
      // Knižnica mení hrdinu často — tie kroky do histórie prehliadača nepatria.
      const hash = routeHash(next);
      if (next.name === 'library' && route.name === 'library') {
        window.history.replaceState(null, '', hash);
      } else {
        window.location.hash = hash;
      }
      setRoute(next);
      window.scrollTo(0, 0);
    },
    [route.name],
  );

  const openMatch = useCallback((id: string) => go({ name: 'match', id }), [go]);
  const home = useCallback(() => go({ name: 'home' }), [go]);
  const portal = useCallback(() => go({ name: 'portal' }), [go]);
  /** Navigácia adresou — pre moduly hier, ktoré nepoznajú typ `Route`. */
  const navigate = useCallback((hash: string) => go(parseRoute(hash)), [go]);

  const logout = useCallback(() => {
    void (async () => {
      await auth.logout().catch(() => undefined);
      setUser(null);
      portal();
    })();
  }, [portal]);

  if (checking) return <div className="screen-message">Načítavam…</div>;
  if (!user) return <Gate onSignedIn={setUser} />;
  // Zápas má vlastnú hlavičku — hra potrebuje každý pixel výšky.
  if (route.name === 'match') return <Game matchId={route.id} onLeave={home} />;

  // Portál má vlastný „útulný" vzhľad, obrazovky Unmatched svoje pozadie arény.
  const section =
    route.name === 'portal' || route.name === 'changelog' ? 'portal' : route.name === 'dune' ? 'dune' : route.name === 'karak'
        ? 'karak'
        : route.name === 'mura'
          ? 'mura'
          : 'unmatched';

  return (
    <div className={`shell shell--${section}`}>
      <AppHeader route={route} nick={user.nick} onNavigate={go} onLogout={logout} />
      <main className="shell__main">
        {route.name === 'portal' ? (
          <Portal nick={user.nick} navigate={navigate} />
        ) : route.name === 'changelog' ? (
          <Changelog scope={route.scope ?? null} onScope={(scope) => go({ name: 'changelog', ...(scope ? { scope } : {}) })} />
        ) : route.name === 'dune' ? (
          // Dune sa načíta až tu — kód ostatných hier sa do Unmatched nebalí.
          <Suspense fallback={<div className="screen-message">Načítavam…</div>}>
            <DuneApp path={route.path} navigate={navigate} />
          </Suspense>
        ) : route.name === 'karak' ? (
          <Suspense fallback={<div className="screen-message">Načítavam…</div>}>
            <KarakApp path={route.path} navigate={navigate} />
          </Suspense>
        ) : route.name === 'mura' ? (
          <Suspense fallback={<div className="screen-message">Načítavam…</div>}>
            <MuraApp path={route.path} navigate={navigate} />
          </Suspense>
        ) : route.name === 'new' ? (
          <NewTable
            key={route.mode ?? ''}
            nick={user.nick}
            initialMode={route.mode === 'bot' ? 'solo' : route.mode}
            onOpenMatch={openMatch}
            onCancel={home}
          />
        ) : route.name === 'matches' ? (
          <Matches nick={user.nick} onOpenMatch={openMatch} />
        ) : route.name === 'library' ? (
          <Library heroId={route.heroId} onHero={(heroId) => go({ name: 'library', heroId })} />
        ) : (
          <Home nick={user.nick} onOpenMatch={openMatch} onNavigate={go} />
        )}
      </main>
      <footer className="shell__foot">
        <span>
          Hearth &amp; Table · dustbox ·{' '}
          <a href="#/patch-notes" className="shell__footLink">
            Patch notes
          </a>
        </span>
        <span>Fanúšikovský projekt pre partiu priateľov</span>
      </footer>
    </div>
  );
}
